import { Box } from "@mui/material";
import React from "react";
import ReactECharts from "echarts-for-react";
import Grid from "@metacrm/metacrm-material-ui/dist/Grid";
import { customerColors } from "features/configure/chartColors";
import EmptyIconStatus from "components/EmptyState/EmptyIconStatus";
import LoadingIcon from "components/LoadingComponent/LoadingIcon";
import IconTooltip from "@metacrm/metacrm-material-ui/dist/IconTooltip";
import { useIntl } from "react-intl";

// 註解一下
// Circle Chart Component
// props: reverse - boolean - default false - if true, the circle chart will be displayed in reverse order.
// props: data - array - required - the data to be displayed in the circle chart.

export default function BehaviorCircleChart({
  reverse = false,
  data = [],
  chartName,
  average,
  tooltip,
  loading = false,
}) {
  const { formatMessage } = useIntl();

  const colors = customerColors.middle;
  const option = {
    color: colors,
    tooltip: {
      trigger: "item",
      formatter: "{a} <br/>{b}: {c}%",
      position: function (point, params, dom, rect, size) {
        // 判斷 Tooltip 是否超出左邊界
        const [x, y] = point; // Tooltip 的觸發點
        const tooltipWidth = size.contentSize[0]; // Tooltip 的寬度

        if (x < tooltipWidth) {
          return [x + 10, y]; // 如果超出左邊界，向右移動
        }
        return [x - tooltipWidth - 10, y]; // 否則正常顯示在左側
      },
    },

    series: [
      {
        name: chartName,
        type: "pie",
        radius: ["65%", "90%"],
        center: ["50%", "50%"], // 中心位置調整為百分比，以便適應不同大小的容器
        label: {
          show: true,
          position: "center",
          formatter: [
            `{a|${formatMessage({ id: "global.chart.tag.average" })}}`,
            `{b|${average}}`,
          ].join("\n"),
          rich: {
            a: {
              align: "center",
              fontSize: 14,
              fontWeight: "500",
              color: "#555",
            },
            b: {
              align: "center",
              fontSize: 18,
              fontWeight: "bold",
              color: "#274B93",
              padding: [4, 0, 0, 0],
            },
          },
        },
        labelLine: {
          show: false,
        },
        data,
      },
    ],
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "250px",
        height: "100%",
        backgroundColor: "#FFF",
        borderRadius: "4px",
        p: "20px 16px 24px",
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box fontSize="16px" fontWeight="500" mb="16px" color="#555555">
        {chartName} {tooltip && <IconTooltip content={tooltip} />}
      </Box>
      {loading ? (
        <LoadingIcon isAbsolute />
      ) : data.length > 0 ? (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: reverse ? "row-reverse" : "row",
              alignItems: "center",
              flex: 1,
            }}
          >
            <Box
              sx={{
                width: "50%",
                height: "100%",
                flexShrink: 0,
                padding: "10px",
              }}
            >
              <ReactECharts
                option={option}
                autoResize={true}
                style={{ height: "200px", width: "100%" }}
                notMerge={true}
              />
            </Box>
            <Box
              sx={{
                width: "50%",
                color: "#777",
                fontWeight: 400,
              }}
            >
              <Grid container spacing={1}>
                {data?.slice(0, 10).map((item, index) => (
                  <Grid
                    xs={12}
                    item
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: "12px",
                      px: "4px",
                    }}
                    key={index}
                  >
                    <Box
                      sx={{
                        height: "8px",
                        width: "13px",
                        borderRadius: "2px",
                        backgroundColor: colors[index],
                        mb: "1px",
                        mr: "8px",
                        flexShrink: 0,
                      }}
                    />
                    <Box key={index}>
                      {item.name}: {item.value}%
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        </>
      ) : (
        <Box height="210px">
          <EmptyIconStatus icon="pie" wording="later" />
        </Box>
      )}
    </Box>
  );
}
